import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import classNames from 'classnames';
import { z } from 'zod';

import { KEYBOARD_KEYS } from '@/shared/lib/constants';

import { type ConnectorConfig, type ConnectorField } from '@/entities/connector';
import {
  normalizeConnectorConfigByFields,
  partitionConnectorConfigByFields,
  validateConfigWithFieldRules,
} from '@/entities/connector/lib/connectorFieldRules';

import styles from './ConfigJsonView.module.css';

type ConfigJsonViewProps = {
  idPrefix: string;
  value: ConnectorConfig;
  fields: readonly ConnectorField[];
  onChange: (config: ConnectorConfig, invalidMappedKeys?: string[]) => void;
  onErrorChange?: (hasError: boolean) => void;
  disabled?: boolean;
  schema?: z.ZodType<ConnectorConfig>;
  showConfigJson: boolean;
};

const DEFAULT_SCHEMA = z.record(z.string(), z.unknown());
const JSON_EDITOR_PLACEHOLDER = '{}';

type ParseResult = { ok: true; value: ConnectorConfig } | { ok: false; errorMessage: string };

const toFormattedJson = (value: ConnectorConfig) => JSON.stringify(value ?? {}, null, 2);

const validateConfigObject = (
  config: ConnectorConfig,
  schema: z.ZodType<ConnectorConfig>,
  fields: readonly ConnectorField[]
): ParseResult => {
  const result = schema.safeParse(config);
  if (!result.success) {
    return { ok: false, errorMessage: '설정은 객체 형태의 JSON이어야 합니다' };
  }

  const fieldValidation = validateConfigWithFieldRules(result.data, fields);
  if (!fieldValidation.ok) {
    return { ok: false, errorMessage: fieldValidation.errorMessage };
  }

  return { ok: true, value: result.data };
};

const parseJsonObject = (
  raw: string,
  schema: z.ZodType<ConnectorConfig>
): { ok: true; value: ConnectorConfig } | { ok: false; errorMessage: string } => {
  try {
    const parsed = JSON.parse(raw);

    const result = schema.safeParse(parsed);
    if (!result.success) {
      return { ok: false, errorMessage: '설정은 객체 형태의 JSON이어야 합니다' };
    }

    return { ok: true, value: result.data };
  } catch {
    return { ok: false, errorMessage: '유효한 JSON이 아닙니다' };
  }
};

export const ConfigJsonView = ({
  idPrefix,
  value,
  fields,
  onChange,
  onErrorChange,
  disabled = false,
  schema = DEFAULT_SCHEMA,
  showConfigJson,
}: ConfigJsonViewProps) => {
  const jsonEditorTextAreaId = `${idPrefix}-json-editor-textarea`;
  const jsonEditorGutterId = `${idPrefix}-json-editor-gutter`;
  const jsonEditorErrorId = `${idPrefix}-json-editor-error`;

  const gutterRef = useRef<HTMLDivElement | null>(null);
  const preserveRawTextOnNextValueSyncRef = useRef(false);

  const [rawText, setRawText] = useState(() => toFormattedJson(value ?? {}));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const lineNumbers = useMemo(() => {
    const lineCount = rawText.split(KEYBOARD_KEYS.NEWLINE).length;
    return Array.from({ length: lineCount }, (_, index) => index + 1);
  }, [rawText]);

  const handleRawChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextRawText = e.target.value;
      const trimmed = nextRawText.trim();

      setRawText(nextRawText);

      if (!trimmed) {
        setErrorMessage('유효한 JSON이 아닙니다');
        onErrorChange?.(true);
        return;
      }

      const result = parseJsonObject(trimmed, schema);
      if (!result.ok) {
        setErrorMessage(result.errorMessage);
        onErrorChange?.(true);
        return;
      }

      const { invalidFieldMessages, validMappedConfig, unmappedConfig, missingMappedKeys } =
        partitionConnectorConfigByFields(result.value, fields);
      const invalidMappedKeys = Object.keys(invalidFieldMessages);

      const nextConfig = {
        ...value,
        ...validMappedConfig,
        ...unmappedConfig,
      };

      for (const missingMappedKey of missingMappedKeys) {
        nextConfig[missingMappedKey] = '';
      }

      preserveRawTextOnNextValueSyncRef.current = true;

      if (invalidMappedKeys.length > 0) {
        onChange(nextConfig, invalidMappedKeys);
        setErrorMessage(invalidFieldMessages[invalidMappedKeys[0]]);
        onErrorChange?.(true);
        return;
      }

      onChange(result.value);
      setErrorMessage(null);
      onErrorChange?.(false);
    },
    [fields, onChange, onErrorChange, schema, value]
  );

  const handleRawScroll = useCallback((event: UIEvent<HTMLTextAreaElement>) => {
    if (!gutterRef.current) {
      return;
    }

    gutterRef.current.scrollTop = event.currentTarget.scrollTop;
  }, []);

  useEffect(() => {
    if (preserveRawTextOnNextValueSyncRef.current) {
      preserveRawTextOnNextValueSyncRef.current = false;
      return;
    }

    const nextValue = normalizeConnectorConfigByFields(fields, value);
    const validation = validateConfigObject(nextValue, schema, fields);

    setRawText(toFormattedJson(nextValue));
    setErrorMessage(validation.ok ? null : validation.errorMessage);
    onErrorChange?.(!validation.ok);
  }, [fields, onErrorChange, schema, showConfigJson, value]);

  return (
    <Form.Item
      className={classNames(styles.formItem, { [styles.hidden]: !showConfigJson })}
      validateStatus={errorMessage ? 'error' : undefined}
      help={
        errorMessage && (
          <span id={jsonEditorErrorId} className={styles.errorMessage} role="alert">
            {errorMessage}
          </span>
        )
      }>
      <div className={classNames(styles.editorShell, errorMessage && styles.error)}>
        <div id={jsonEditorGutterId} ref={gutterRef} className={styles.gutter} aria-hidden="true">
          {lineNumbers.map((lineNumber) => (
            <div key={lineNumber} className={styles.gutterLine}>
              {lineNumber}
            </div>
          ))}
        </div>
        <TextArea
          id={jsonEditorTextAreaId}
          value={rawText}
          onChange={handleRawChange}
          onScroll={handleRawScroll}
          disabled={disabled}
          className={styles.textarea}
          aria-invalid={Boolean(errorMessage)}
          aria-describedby={errorMessage ? jsonEditorErrorId : undefined}
          placeholder={JSON_EDITOR_PLACEHOLDER}
          spellCheck={false}
        />
      </div>
    </Form.Item>
  );
};
